// @givmo/app-admin — signing in through the person's organization (AS-WEB30): the step the chooser's
// "Sign in with Organization SSO" opens (`/sso`), and the portal's `/sso/callback`, where the employer's
// sign-in page sends the browser back.
//
// The step and the callback are `@givmo/passkeys`' (frames W2–W4, WF1–WF5); this file places them in
// the portal's card and lands the sign-in the way every other portal sign-in lands: the routing state
// `adminSignInWithOrganizationSso` resolved (`navigationForRoutingState`, honouring `redirect`), or —
// for an account with no company — the companyless landing (EP-SUITE2-FIX5), because that sign-in
// succeeded and is not a failure.
//
// A same-tab return that could not finish comes back to the step carrying the typed address and the
// reason in history state (`organizationSso`), never in the address bar.

import { useCallback } from 'react';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { sanitizeRedirectTarget } from '@givmo/org-console';
import type { AdminSignInResult } from '@givmo/auth';
import {
  ORGANIZATION_SSO_CALLBACK_PATH,
  OrganizationSsoCallback,
  OrganizationSsoFlow,
  organizationSsoReturnOf,
  type OrganizationSsoCallbackFailure,
  type OrganizationSsoReturnState,
} from '@givmo/passkeys';
import { useAuth } from '../../auth/auth-context';
import { AuthShell } from './auth-shell';
import { AUTH_ROUTES, navigationForRoutingState } from './auth-routing';
import { landSignInWithoutCompany, type LandingTarget } from './companyless-landing';

declare module '@tanstack/react-router' {
  interface HistoryState {
    /** A same-tab company sign-in that could not finish (AS-WEB30): what was typed, and why. */
    organizationSso?: OrganizationSsoReturnState;
  }
}

/** This portal's door heading, so the step reads as that door's next screen. */
const HEADING = 'font-sans text-s22 font-w700 tracking-givmo text-text-strong';

/** How a portal sign-in lands: its resolved routing state, or the companyless landing. */
type PortalSignIn =
  | { kind: 'resolved'; result: AdminSignInResult }
  | { kind: 'companyless'; landing: LandingTarget };

/** The context's sign-in, with a sign-in that found no company landed rather than failed. */
function usePortalOrganizationSignIn() {
  const { signInWithOrganizationSso } = useAuth();
  const queryClient = useQueryClient();
  return useCallback(
    async (params: { handoffCode: string; codeVerifier: string }): Promise<PortalSignIn> => {
      try {
        return { kind: 'resolved', result: await signInWithOrganizationSso(params) };
      } catch (thrown) {
        const landing = await landSignInWithoutCompany(thrown, queryClient);
        if (landing !== null) return { kind: 'companyless', landing };
        throw thrown;
      }
    },
    [signInWithOrganizationSso, queryClient],
  );
}

/** Land a portal sign-in where every other portal sign-in would. */
function usePortalLanding() {
  const navigate = useNavigate();
  return useCallback(
    (signedIn: PortalSignIn, redirect: string | undefined) =>
      void navigate(
        signedIn.kind === 'companyless'
          ? signedIn.landing
          : navigationForRoutingState(signedIn.result.state, redirect),
      ),
    [navigate],
  );
}

export function OrganizationSsoScreen() {
  const navigate = useNavigate();
  const signIn = usePortalOrganizationSignIn();
  const land = usePortalLanding();
  const { redirect, back } = useSearch({ strict: false }) as { redirect?: string; back?: string };
  const here = useLocation({ select: (location) => location.href });
  // Read once by the step, as its starting address and reason.
  const carried = organizationSsoReturnOf(useLocation({ select: (location) => location.state }));
  const target = sanitizeRedirectTarget(redirect);

  return (
    <AuthShell testId="page-organization-sso">
      <OrganizationSsoFlow
        headingClass={HEADING}
        signIn={signIn}
        onSignedIn={(signedIn) => land(signedIn, target)}
        returnTo={target ?? AUTH_ROUTES.dashboard}
        onBackToSignIn={() =>
          void navigate({ to: sanitizeRedirectTarget(back) ?? AUTH_ROUTES.loginHome })
        }
        backTo={sanitizeRedirectTarget(back)}
        onLostAccess={() =>
          void navigate({ to: AUTH_ROUTES.lostCompanyAccess, search: { back: here } })
        }
        initialEmail={carried?.email}
        initialFailure={carried?.failure}
      />
    </AuthShell>
  );
}

/** `/sso/callback`: the employer's sign-in page sends the browser back here (frame W4). */
export function OrganizationSsoCallbackScreen() {
  const navigate = useNavigate();
  const signIn = usePortalOrganizationSignIn();
  const land = usePortalLanding();
  const { handoff_code: handoffCode, error } = useSearch({ strict: false }) as {
    handoff_code?: string;
    error?: string;
  };

  const onSignedIn = useCallback(
    (returnTo: string, signedIn: PortalSignIn) => land(signedIn, sanitizeRedirectTarget(returnTo)),
    [land],
  );
  // Back to the step with what was typed and why (WF1–WF5), and its own way back (cut 1007e's review,
  // F5). The portal has no director register.
  const onFailed = useCallback(
    ({ email, failure, returnTo, back }: OrganizationSsoCallbackFailure) => {
      const redirect = sanitizeRedirectTarget(returnTo ?? undefined);
      const stepBack = sanitizeRedirectTarget(back ?? undefined);
      void navigate({
        to: AUTH_ROUTES.organizationSso,
        search: { ...(redirect ? { redirect } : {}), ...(stepBack ? { back: stepBack } : {}) },
        replace: true,
        state: { organizationSso: { email, failure } },
      });
    },
    [navigate],
  );
  // The handoff code is one-use: out of the address bar as soon as it is read.
  const onRead = useCallback(
    () => void navigate({ to: ORGANIZATION_SSO_CALLBACK_PATH, search: {}, replace: true }),
    [navigate],
  );

  return (
    <AuthShell testId="page-organization-sso-callback">
      <OrganizationSsoCallback
        handoffCode={handoffCode ?? null}
        error={error ?? null}
        headingClass={HEADING}
        signIn={signIn}
        onSignedIn={onSignedIn}
        onFailed={onFailed}
        onRead={onRead}
      />
    </AuthShell>
  );
}
